'use client';

import { useState } from 'react';
import BookingCard from './BookingCard';
import DocumentWallet from './DocumentWallet';
import TripTimeline from './TripTimeline';
import ModificationPanel from './ModificationPanel';
import InsuranceCard from './InsuranceCard';
import EmergencyContacts from './EmergencyContacts';
import CheckInReminder from './CheckInReminder';
import LoyaltyProgram from './LoyaltyProgram';
import BookingFilters from './BookingFilters';
import ClaudeChatWidget from '@/components/common/ClaudeChatWidget';
import EmbassyStatusChecker from './EmbassyStatusChecker';

interface Booking {
  id: string;
  pnr: string;
  destination: string;
  departureDate: string;
  returnDate: string;
  status: 'confirmed' | 'pending' | 'cancelled';
  travelers: number;
  totalAmount: number;
  currency: string;
  image: string;
  alt: string;
  airline: string;
  flightNumber: string;
}

interface Document {
  id: string;
  type: string;
  name: string;
  icon: string;
  size: string;
  downloadUrl: string;
}

interface TimelineEvent {
  id: string;
  type: 'flight' | 'hotel' | 'activity' | 'transfer';
  title: string;
  time: string;
  location: string;
  status: 'completed' | 'upcoming' | 'current';
  details: string;
}

interface ModificationOption {
  id: string;
  type: string;
  label: string;
  description: string;
  icon: string;
  fee: number;
}

interface InsurancePlan {
  id: string;
  name: string;
  coverage: string[];
  price: number;
  recommended: boolean;
}

interface Contact {
  id: string;
  name: string;
  relationship: string;
  phone: string;
  email: string;
}

interface Flight {
  id: string;
  airline: string;
  flightNumber: string;
  departure: string;
  checkInOpens: string;
  checkInUrl: string;
}

interface LoyaltyTier {
  name: string;
  points: number;
  nextTier: string;
  pointsToNext: number;
  benefits: string[];
}

interface FilterState {
  status: string[];
  dateRange: string;
  sortBy: string;
}

export default function BookingManagementInteractive() {
  const [activeTab, setActiveTab] = useState<'bookings' | 'documents' | 'timeline' | 'embassy'>('bookings');
  const [filters, setFilters] = useState<FilterState>({
    status: [],
    dateRange: 'all',
    sortBy: 'date-desc'
  });

  const mockBookings: Booking[] = [
  {
    id: 'BK001',
    pnr: 'TG7X9K2',
    destination: 'Paris, France',
    departureDate: 'Feb 15, 2026',
    returnDate: 'Feb 22, 2026',
    status: 'confirmed',
    travelers: 2,
    totalAmount: 3450,
    currency: '$',
    image: "https://images.unsplash.com/photo-1552220348-65caf44b4954",
    alt: 'Eiffel Tower illuminated at sunset with Seine River in foreground',
    airline: 'Air France',
    flightNumber: 'AF1234'
  },
  {
    id: 'BK002',
    pnr: 'TG4M8N1',
    destination: 'Tokyo, Japan',
    departureDate: 'Mar 10, 2026',
    returnDate: 'Mar 20, 2026',
    status: 'pending',
    travelers: 1,
    totalAmount: 2890,
    currency: '$',
    image: "https://images.unsplash.com/photo-1681317473971-06d8fae2264f",
    alt: 'Mount Fuji with cherry blossoms in full bloom and traditional Japanese temple',
    airline: 'Japan Airlines',
    flightNumber: 'JL5678'
  },
  {
    id: 'BK003',
    pnr: 'TG2P5Q7',
    destination: 'Dubai, UAE',
    departureDate: 'Apr 05, 2026',
    returnDate: 'Apr 12, 2026',
    status: 'confirmed',
    travelers: 4,
    totalAmount: 5670,
    currency: '$',
    image: "https://images.unsplash.com/photo-1583973508868-74c8440079c9",
    alt: 'Burj Al Arab luxury hotel at dusk with golden sky and palm trees',
    airline: 'Emirates',
    flightNumber: 'EK9012'
  }];


  const mockDocuments: Document[] = [
  {
    id: 'DOC001',
    type: 'E-Ticket',
    name: 'Flight Confirmation - AF1234',
    icon: 'TicketIcon',
    size: '245 KB',
    downloadUrl: '#'
  },
  {
    id: 'DOC002',
    type: 'Hotel Voucher',
    name: 'Hotel Le Meurice Paris',
    icon: 'BuildingOfficeIcon',
    size: '189 KB',
    downloadUrl: '#'
  },
  {
    id: 'DOC003',
    type: 'Travel Insurance',
    name: 'Comprehensive Coverage Policy',
    icon: 'ShieldCheckIcon',
    size: '312 KB',
    downloadUrl: '#'
  },
  {
    id: 'DOC004',
    type: 'Boarding Pass',
    name: 'Mobile Boarding Pass - AF1234',
    icon: 'DevicePhoneMobileIcon',
    size: '156 KB',
    downloadUrl: '#'
  }];


  const mockTimeline: TimelineEvent[] = [
  {
    id: 'TL001',
    type: 'flight',
    title: 'Departure Flight',
    time: 'Feb 15, 10:30 AM',
    location: 'JFK Airport, New York',
    status: 'upcoming',
    details: 'Air France AF1234 - Check-in opens 24 hours before departure'
  },
  {
    id: 'TL002',
    type: 'transfer',
    title: 'Airport Transfer',
    time: 'Feb 15, 6:45 PM',
    location: 'Charles de Gaulle Airport',
    status: 'upcoming',
    details: 'Private car service to hotel - Driver will meet you at arrivals'
  },
  {
    id: 'TL003',
    type: 'hotel',
    title: 'Hotel Check-in',
    time: 'Feb 15, 8:00 PM',
    location: 'Hotel Le Meurice',
    status: 'upcoming',
    details: 'Deluxe Suite with Eiffel Tower view - Early check-in confirmed'
  },
  {
    id: 'TL004',
    type: 'activity',
    title: 'Louvre Museum Tour',
    time: 'Feb 17, 2:00 PM',
    location: 'Musée du Louvre',
    status: 'upcoming',
    details: 'Private guided tour with skip-the-line access - Duration: 3 hours'
  },
  {
    id: 'TL005',
    type: 'flight',
    title: 'Return Flight',
    time: 'Feb 22, 3:15 PM',
    location: 'Charles de Gaulle Airport',
    status: 'upcoming',
    details: 'Air France AF1235 - Arrive JFK at 6:45 PM local time'
  }];


  const mockModifications: ModificationOption[] = [
  {
    id: 'MOD001',
    type: 'date',
    label: 'Change Travel Dates',
    description: 'Modify departure or return dates with real-time availability',
    icon: 'CalendarIcon',
    fee: 150
  },
  {
    id: 'MOD002',
    type: 'upgrade',
    label: 'Upgrade Cabin Class',
    description: 'Move to Business or First Class with instant pricing',
    icon: 'ArrowUpCircleIcon',
    fee: 850
  },
  {
    id: 'MOD003',
    type: 'passenger',
    label: 'Add/Remove Travelers',
    description: 'Update passenger count and details',
    icon: 'UserGroupIcon',
    fee: 75
  },
  {
    id: 'MOD004',
    type: 'hotel',
    label: 'Change Accommodation',
    description: 'Switch hotels or upgrade room category',
    icon: 'BuildingOfficeIcon',
    fee: 100
  }];


  const mockInsurance: InsurancePlan[] = [
  {
    id: 'INS001',
    name: 'Essential Coverage',
    coverage: [
    'Trip cancellation up to $5,000',
    'Medical emergency coverage',
    'Lost baggage reimbursement',
    '24/7 travel assistance'],

    price: 89,
    recommended: false
  },
  {
    id: 'INS002',
    name: 'Premium Protection',
    coverage: [
    'Trip cancellation up to $10,000',
    'Medical emergency & evacuation',
    'Lost baggage & travel delays',
    'Cancel for any reason coverage',
    'Adventure sports coverage',
    'Rental car protection'],

    price: 159,
    recommended: true
  }];


  const mockContacts: Contact[] = [
  {
    id: 'CON001',
    name: 'Sarah Johnson',
    relationship: 'Spouse',
    phone: '+1 (555) 123-4567',
    email: 'sarah.johnson@email.com'
  },
  {
    id: 'CON002',
    name: 'Michael Chen',
    relationship: 'Brother',
    phone: '+1 (555) 987-6543',
    email: 'michael.chen@email.com'
  }];


  const mockFlights: Flight[] = [
  {
    id: 'FL001',
    airline: 'Air France',
    flightNumber: 'AF1234',
    departure: 'Feb 15, 2026 10:30 AM',
    checkInOpens: 'in 14 days',
    checkInUrl: '#'
  },
  {
    id: 'FL002',
    airline: 'Air France',
    flightNumber: 'AF1235',
    departure: 'Feb 22, 2026 3:15 PM',
    checkInOpens: 'in 21 days',
    checkInUrl: '#'
  }];


  const mockLoyalty: LoyaltyTier = {
    name: 'Gold',
    points: 12450,
    nextTier: 'Platinum',
    pointsToNext: 7550,
    benefits: [
    'Priority check-in and boarding',
    'Extra baggage allowance (2 bags)',
    'Complimentary seat selection',
    'Access to airport lounges',
    '15% discount on future bookings',
    'Dedicated customer support line']

  };

  const filterBookings = (bookings: Booking[]): Booking[] => {
    let filtered = [...bookings];

    if (filters.status.length > 0) {
      filtered = filtered.filter((b) => filters.status.includes(b.status));
    }

    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'date-desc':
          return new Date(b.departureDate).getTime() - new Date(a.departureDate).getTime();
        case 'date-asc':
          return new Date(a.departureDate).getTime() - new Date(b.departureDate).getTime();
        case 'amount-desc':
          return b.totalAmount - a.totalAmount;
        case 'amount-asc':
          return a.totalAmount - b.totalAmount;
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredBookings = filterBookings(mockBookings);

  return (
    <div className="min-h-screen bg-background pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading font-bold text-4xl text-foreground mb-2">
            Booking Management
          </h1>
          <p className="text-lg text-muted-foreground">
            Complete trip oversight from PNR to post-travel
          </p>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          <button
            onClick={() => setActiveTab('bookings')}
            className={`px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-300 whitespace-nowrap ${
            activeTab === 'bookings' ? 'bg-primary text-primary-foreground shadow-brand' : 'bg-surface text-muted-foreground hover:bg-muted'}`
            }>
            
            My Bookings
          </button>
          <button
            onClick={() => setActiveTab('documents')}
            className={`px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-300 whitespace-nowrap ${
            activeTab === 'documents' ?
            'bg-primary text-primary-foreground shadow-brand' : 'bg-surface text-muted-foreground hover:bg-muted'}`
            }>
            
            Documents
          </button>
          <button
            onClick={() => setActiveTab('timeline')}
            className={`px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-300 whitespace-nowrap ${
            activeTab === 'timeline' ? 'bg-primary text-primary-foreground shadow-brand' : 'bg-surface text-muted-foreground hover:bg-muted'}`
            }>
            
            Trip Timeline
          </button>
          <button
            onClick={() => setActiveTab('embassy')}
            className={`px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-300 whitespace-nowrap ${
            activeTab === 'embassy' ? 'bg-primary text-primary-foreground shadow-brand' : 'bg-surface text-muted-foreground hover:bg-muted'}`
            }>
            
            Embassy Status
          </button>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {activeTab === 'bookings' &&
            <>
                <div className="grid grid-cols-1 gap-6">
                  {filteredBookings.map((booking) =>
                <BookingCard key={booking.id} booking={booking} />
                )}
                </div>
                {filteredBookings.length === 0 &&
              <div className="bg-card rounded-xl shadow-brand p-12 text-center border border-border">
                    <p className="text-muted-foreground">No bookings match your filters</p>
                  </div>
              }
              </>
            }

            {activeTab === 'documents' &&
            <div className="space-y-6">
                <DocumentWallet documents={mockDocuments} />
                <CheckInReminder flights={mockFlights} />
              </div>
            }

            {activeTab === 'timeline' &&
            <div className="space-y-6">
                <TripTimeline events={mockTimeline} />
                <ModificationPanel options={mockModifications} currency="$" />
              </div>
            }

            {activeTab === 'embassy' &&
            <div className="space-y-6">
                <EmbassyStatusChecker bookings={filteredBookings} />
              </div>
            }
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {activeTab === 'bookings' &&
            <>
                <BookingFilters onFilterChange={setFilters} />
                <LoyaltyProgram tier={mockLoyalty} />
              </>
            }

            {activeTab === 'documents' &&
            <>
                <EmergencyContacts contacts={mockContacts} />
                <InsuranceCard plans={mockInsurance} currency="$" />
              </>
            }

            {activeTab === 'timeline' &&
            <>
                <ModificationPanel options={mockModifications} currency="$" />
                <EmergencyContacts contacts={mockContacts} />
              </>
            }
          </div>
        </div>
      </div>
      <ClaudeChatWidget
        context={{
          page: 'booking-management'
        }} />

    </div>);


}